import { Send, CheckCheck, Wallet } from 'lucide-react';
import { formatCurrency } from '../../../utils/format';
import { useLanguage } from '../../../features/language/useLanguage';
import type { SalesOrder } from '../../../types/salesOrder';
import { SalesOrderStatusBadge } from './SalesOrderStatusBadge';
import { SalesOrderPaymentBadge } from './SalesOrderPaymentBadge';
import type { SalesOrderRowActions } from './types';

type SalesOrderMobileCardProps = { so: SalesOrder } & SalesOrderRowActions;

/** Mobile (card) presentation of a single sales order. */
export function SalesOrderMobileCard({
  so,
  canWrite,
  currencySymbol,
  onOpen,
  onCustomerClick,
  onMarkAs,
  onPay,
}: SalesOrderMobileCardProps) {
  const { t } = useLanguage();
  const paymentStatus = so.paymentStatus ?? 'unpaid';
  return (
    <div
      onClick={() => onOpen(so)}
      className="bg-white p-4 rounded-lg shadow border border-gray-200 space-y-3 cursor-pointer active:bg-gray-50"
    >
      <div className="flex justify-between items-start">
        <div>
          <span className="text-xs text-gray-500 block mb-1" data-test-id={`so-number-mobile-${so.id}`}>
            {so.orderNumber}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCustomerClick(so.customer);
            }}
            className="font-medium text-primary-600 hover:text-primary-800"
          >
            {so.customer}
          </button>
        </div>
        <div className="flex flex-col items-end gap-1">
          <SalesOrderStatusBadge status={so.status} className="px-2 py-1 text-xs rounded-full" />
          {canWrite && paymentStatus !== 'unpaid' ? (
            <button
              type="button"
              data-test-id={`so-payment-badge-mobile-${so.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onPay(so);
              }}
              className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
            >
              <SalesOrderPaymentBadge
                paymentStatus={paymentStatus}
                className="px-2 py-1 text-xs rounded-full cursor-pointer"
              />
            </button>
          ) : (
            <SalesOrderPaymentBadge paymentStatus={paymentStatus} className="px-2 py-1 text-xs rounded-full" />
          )}
        </div>
      </div>

      <div className="flex justify-between items-center text-sm pt-2 border-t border-gray-100">
        <span className="text-gray-500">{t('salesOrder.list.totalAmount')}</span>
        <span className="font-semibold text-gray-900">{formatCurrency(so.totalAmount, currencySymbol)}</span>
      </div>

      {so.internalNotes && (
        <p className="text-sm text-gray-500 italic bg-gray-50 p-2 rounded">{so.internalNotes}</p>
      )}

      {canWrite &&
        (so.status === 'Draft' ||
          so.status === 'Sent' ||
          (paymentStatus === 'unpaid' && so.status !== 'Cancelled')) && (
        <div className="pt-2 border-t border-gray-100 flex flex-wrap justify-end gap-2">
          {so.status === 'Draft' && (
            <button
              data-test-id={`so-mobile-mark-sent-${so.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onMarkAs(so, 'Sent');
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors border border-blue-200"
            >
              <Send className="h-3.5 w-3.5" />
              {t('salesOrder.list.markAsSent')}
            </button>
          )}
          {so.status === 'Sent' && (
            <button
              data-test-id={`so-mobile-mark-delivered-${so.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onMarkAs(so, 'Delivered');
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors border border-emerald-200"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              {t('salesOrder.list.markAsDelivered')}
            </button>
          )}
          {paymentStatus === 'unpaid' && (
            <button
              data-test-id={`so-mobile-mark-paid-${so.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onPay(so, 'paid');
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors border border-primary-200"
            >
              <Wallet className="h-3.5 w-3.5" />
              {t('salesOrder.payment.markAsPaid')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
